import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Layout } from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TrackingInfoModal } from '@/components/TrackingInfoModal';
import { GameChatModal } from '@/components/GameChatModal';
import { KeyRequestsList } from '@/components/KeyRequestsList';
import { CoverageContent } from '@/components/CoverageContent';
import {
	Gamepad2,
	TrendingUp,
	Key,
	Clock,
	Trophy
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { getCreatorStats, CreatorStats as CreatorStatsType } from '@/services/pointsService';
import { CREATOR_BADGE_TIERS } from '@/data/missions';
import { getSteamStatsFromDB, shouldSyncSteamData, syncSteamDataForCreator } from '@/services/steamSyncService';
import { formatPlaytime } from '@/services/steamPlaytimeService';

interface GameWithPlaytime {
	id: string;
	game_id: string;
	game: {
		id: string;
		title: string;
		cover_image_url: string | null;
		steam_url?: string | null;
		steam_game_id?: number | null;
	};
	playtime: number;
	playtime_2weeks: number;
	achievements?: {
		unlocked: number;
		total: number;
	};
}

interface KeyRequestWithGame {
	id: string;
	game_id: string;
	status: 'pending' | 'approved' | 'rejected';
	created_at: string;
	response_message: string | null;
	is_covered: boolean;
	message: string | null;
	game: {
		id: string;
		title: string;
		cover_image_url: string | null;
		hashtag?: string;
		youtube_tag?: string;
		twitch_category?: string;
		keymailer_hashtag?: string;
		disclosure_text?: string;
		developer: {
			id: string;
			display_name: string;
			studio_name?: string;
			studio_logo_url?: string;
			studio_slug?: string;
		};
	};
}

interface CoverageItem {
	id: string;
	creator_id: string;
	game_id: string;
	key_request_id: string;
	title: string;
	thumbnail_url: string;
	video_url: string;
	platform: 'youtube' | 'twitch';
	posted_date: string;
	views: number;
	tags: string[];
	created_at: string;
	game?: {
		id: string;
		title: string;
		cover_image_url?: string;
	};
}

interface ContentItem {
	id: string;
	title: string;
	thumbnail_url: string;
	url: string;
	platform: 'youtube' | 'twitch';
	published_at: string;
	view_count?: number;
	duration?: string;
	tags?: string[];
}

interface UserProfile {
	twitch_connected: boolean;
	youtube_connected: boolean;
	twitch_username?: string;
	youtube_username?: string;
}

export default function CreatorProfile() {
	const { user, userType } = useAuth();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const defaultTab = searchParams.get('tab') || 'overview';
	const [activeTab, setActiveTab] = useState(defaultTab);
	const [creatorStats, setCreatorStats] = useState<CreatorStatsType | null>(null);
	const [keyRequests, setKeyRequests] = useState<KeyRequestWithGame[]>([]);
	const [loading, setLoading] = useState(true);
	const [gamesWithPlaytime, setGamesWithPlaytime] = useState<GameWithPlaytime[]>([]);

	// Key Requests tab states
	const [selectedGameForTracking, setSelectedGameForTracking] = useState<any>(null);
	const [isViewingTrackingInfo, setIsViewingTrackingInfo] = useState(false);
	const [isViewingChat, setIsViewingChat] = useState(false);

	// Coverage tab states
	const [coverageItems, setCoverageItems] = useState<CoverageItem[]>([]);
	const [userProfile, setUserProfile] = useState<UserProfile | null>(null);

	useEffect(() => {
		if (user?.id) {
			loadCreatorData();
			fetchUserProfile();
		}
	}, [user?.id, userType]);

	useEffect(() => {
		const tab = searchParams.get('tab');
		if (tab && tab !== activeTab) {
			setActiveTab(tab);
		}
	}, [searchParams]);

	const handleTabChange = (value: string) => {
		setActiveTab(value);
		setSearchParams({ tab: value }, { replace: true });
	};

	useEffect(() => {
		if (userProfile && (userProfile.youtube_connected || userProfile.twitch_connected)) {
			fetchCoverageItems();
		}
	}, [userProfile]);

	const loadCreatorData = async () => {
		if (!user) return;

		setLoading(true);
		try {
			// Get creator stats with badges
			const stats = await getCreatorStats(user.id);
			if (stats) {
				setCreatorStats(stats);
			}

			// Get all key requests with game details
			const { data: requests, error: requestsError } = await supabase
				.from('key_requests')
				.select(`
					id,
					game_id,
					status,
					created_at,
					response_message,
					is_covered,
					message,
					game:games!inner(
						id,
						title,
						cover_image_url,
						hashtag,
						youtube_tag,
						twitch_category,
						keymailer_hashtag,
						disclosure_text,
						steam_url,
						steam_game_id,
						developer:profiles!games_developer_id_fkey(
							id,
							display_name,
							studio_name,
							studio_logo_url,
							studio_slug
						)
					)
				`)
				.eq('creator_id', user.id)
				.order('created_at', { ascending: false });

			if (requestsError) {
				console.error('Error fetching key requests:', requestsError);
			} else {
				const requestsData = (requests || []) as any[];
				setKeyRequests(requestsData);

				// Get playtime for approved games
				const approvedRequests = requestsData.filter(r => r.status === 'approved');
				const approvedGameIds = approvedRequests.map(r => r.game_id);

				if (approvedGameIds.length > 0) {
					// Vérifier si une synchronisation Steam est nécessaire (une fois par jour)
					const needsSync = await shouldSyncSteamData(user.id);

					// Si besoin de sync, lancer en arrière-plan (ne pas bloquer l'affichage)
					if (needsSync) {
						syncSteamDataForCreator(user.id).catch(error => {
							console.error('Erreur lors de la synchronisation Steam:', error);
						});
					}

					// Get Steam stats from DB
					const steamStats = await getSteamStatsFromDB(user.id);
					const statsMap = new Map(steamStats.map(stat => [stat.game_id, stat]));

					// Build games with playtime list
					const gamesWithPlaytimeList: GameWithPlaytime[] = [];
					for (const request of approvedRequests) {
						const gameId = request.game_id;
						const gameStats = statsMap.get(gameId);

						// Inclure même si pas de stats (pour afficher le jeu)
						gamesWithPlaytimeList.push({
							id: request.id,
							game_id: gameId,
							game: request.game,
							playtime: gameStats?.playtime_forever || 0,
							playtime_2weeks: gameStats?.playtime_2weeks || 0,
							achievements: gameStats && gameStats.total_achievements > 0 ? {
								unlocked: gameStats.unlocked_achievements || 0,
								total: gameStats.total_achievements || 0
							} : undefined
						});
					}
					setGamesWithPlaytime(gamesWithPlaytimeList);
				}
			}
		} catch (error) {
			console.error('Error loading creator data:', error);
		} finally {
			setLoading(false);
		}
	};

	const fetchUserProfile = async () => {
		if (!user) return;

		try {
			const { data, error } = await supabase
				.from('profiles')
				.select('twitch_connected, youtube_connected, twitch_username, youtube_username')
				.eq('id', user.id)
				.single();

			if (error) {
				console.error('Error fetching user profile:', error);
				return;
			}

			setUserProfile(data as any);
		} catch (error) {
			console.error('Error fetching user profile:', error);
		}
	};

	const fetchCoverageItems = async () => {
		if (!user) return;

		try {
			const { data, error } = await (supabase as any)
				.from("coverage")
				.select("*")
				.eq("creator_id", user.id)
				.order("posted_date", { ascending: false });

			if (error) {
				console.error('Error fetching coverage items:', error);
				return;
			}

			setCoverageItems((data || []) as CoverageItem[]);
		} catch (error) {
			console.error('Error fetching coverage items:', error);
		}
	};

	const handleCancelRequest = async (requestId: string) => {
		try {
			const { error } = await supabase
				.from("key_requests")
				.delete()
				.eq("id", requestId);

			if (error) {
				console.error("Error canceling request:", error);
				toast.error("Failed to cancel request");
				return;
			}

			toast.success("Request canceled successfully");
			loadCreatorData();
		} catch (error) {
			console.error("Error:", error);
			toast.error("An error occurred");
		}
	};

	const handleViewTrackingInfo = async (gameId: string, gameKey: string) => {
		try {
			const { data, error } = await supabase
				.from("games")
				.select(`
					id,
					title,
					hashtag,
					youtube_tag,
					twitch_category,
					keymailer_hashtag,
					disclosure_text,
					developer:profiles!games_developer_id_fkey(
						id,
						display_name,
						studio_name,
						studio_logo_url,
						studio_slug
					)
				`)
				.eq("id", gameId)
				.single();

			if (error || !data) {
				console.error("Error fetching game details:", error);
				toast.error("Failed to load game information");
				return;
			}

			const gameData = data as any;
			setSelectedGameForTracking({
				id: gameData.id,
				title: gameData.title,
				hashtag: gameData.hashtag,
				youtube_tag: gameData.youtube_tag,
				twitch_category: gameData.twitch_category,
				keymailer_hashtag: gameData.keymailer_hashtag,
				disclosure_text: gameData.disclosure_text,
				developer: gameData.developer,
				key: gameKey
			});
			setIsViewingTrackingInfo(true);
		} catch (error) {
			console.error("Error:", error);
			toast.error("An error occurred");
		}
	};


	if (loading) {
		return (
			<Layout>
				<div className="container mx-auto px-4 py-8">
					<div className="animate-pulse space-y-6">
						<div className="h-8 bg-muted rounded w-1/3"></div>
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
							{[1, 2, 3].map((i) => (
								<div key={i} className="h-32 bg-muted rounded"></div>
							))}
						</div>
					</div>
				</div>
			</Layout>
		);
	}

	if (!creatorStats) {
		return (
			<Layout>
				<div className="container mx-auto px-4 py-8">
					<Card className="p-8 text-center">
						<p className="text-muted-foreground">Unable to load your statistics.</p>
					</Card>
				</div>
			</Layout>
		);
	}

	return (
		<Layout>
			<div className="container mx-auto px-4 py-8">
				<div className="mb-8">
					<h1 className="text-3xl font-bold mb-2">My Creator Profile</h1>
					<p className="text-muted-foreground">
						Track your key requests, coverage statistics, and creator performance
					</p>
				</div>

				<Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
					<TabsList className="grid w-full grid-cols-2 md:grid-cols-5 gap-1 h-auto p-1">
						<TabsTrigger value="overview" className="h-10">Overview</TabsTrigger>
						<TabsTrigger value="requests" className="h-10">Key Requests</TabsTrigger>
						<TabsTrigger value="coverage" className="h-10">Coverage</TabsTrigger>
						<TabsTrigger value="playtime" className="h-10">Playtime</TabsTrigger>
						<TabsTrigger value="badges" className="h-10">Badges</TabsTrigger>
					</TabsList>

					<TabsContent value="overview" className="space-y-6">
						{/* Main Stats */}
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
							<Card className="p-6">
								<div className="flex items-center gap-3">
									<Key className="w-8 h-8 text-blue-500" />
									<div className="flex-1">
										<p className="text-sm text-muted-foreground">Total Requests</p>
										<p className="text-xl font-bold">{creatorStats.total_requests}</p>
									</div>
								</div>
							</Card>

							<Card className="p-6">
								<div className="flex items-center gap-3">
									<Key className="w-8 h-8 text-green-500" />
									<div className="flex-1">
										<p className="text-sm text-muted-foreground">Keys Received</p>
										<p className="text-xl font-bold">{creatorStats.keys_received}</p>
									</div>
								</div>
							</Card>

							<Card className="p-6">
								<div className="flex items-center gap-3">
									<Trophy className="w-8 h-8 text-yellow-500" />
									<div className="flex-1">
										<p className="text-sm text-muted-foreground">Badges Earned</p>
										<p className="text-xl font-bold">{creatorStats.badges.length}</p>
									</div>
								</div>
							</Card>

							<Card className="p-6">
								<div className="flex items-center gap-3">
									<Gamepad2 className="w-8 h-8 text-purple-500" />
									<div className="flex-1">
										<p className="text-sm text-muted-foreground">Games Covered</p>
										<p className="text-xl font-bold">{creatorStats.games_covered}</p>
									</div>
								</div>
							</Card>

							<Card className="p-6">
								<div className="flex items-center gap-3">
									<TrendingUp className="w-8 h-8 text-orange-500" />
									<div className="flex-1">
										<p className="text-sm text-muted-foreground">Content Created</p>
										<p className="text-xl font-bold">{creatorStats.content_created}</p>
									</div>
								</div>
							</Card>

							<Card className="p-6">
								<div className="flex items-center gap-3">
									<Clock className="w-8 h-8 text-purple-500" />
									<div className="flex-1">
										<p className="text-sm text-muted-foreground">Total Playtime</p>
										<p className="text-xl font-bold">{Math.round(creatorStats.total_playtime / 60)}h</p>
									</div>
								</div>
							</Card>
						</div>

						{/* Key Request Status Breakdown */}
						<Card className="p-6">
							<h3 className="text-lg font-semibold mb-4">Key Request Status</h3>
							<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
								<div className="text-center bg-muted/30 flex flex-col items-center justify-center rounded-lg p-2">
									<p className="text-2xl font-bold">{creatorStats.total_requests}</p>
									<p className="text-[12px] text-muted-foreground">Total</p>
								</div>
								<div className="text-center bg-green-500/10 flex flex-col items-center justify-center rounded-lg p-2">
									<p className="text-2xl font-bold text-green-600">{creatorStats.approved_requests}</p>
									<p className="text-[12px] text-muted-foreground">Approved</p>
								</div>
								<div className="text-center bg-yellow-500/10 flex flex-col items-center justify-center rounded-lg p-2">
									<p className="text-2xl font-bold text-yellow-600">{creatorStats.pending_requests}</p>
									<p className="text-[12px] text-muted-foreground">Pending</p>
								</div>
								<div className="text-center bg-red-500/10 flex flex-col items-center justify-center rounded-lg p-2">
									<p className="text-2xl font-bold text-red-600">{creatorStats.rejected_requests}</p>
									<p className="text-[12px] text-muted-foreground">Rejected</p>
								</div>
							</div>
						</Card>

						{/* Statistics */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							<Card className="p-6">
								<h3 className="text-lg font-semibold mb-4">Request Statistics</h3>
								<div className="space-y-3">
									<div className="flex justify-between">
										<span className="text-muted-foreground">Approval Rate</span>
										<span className="font-medium">
											{creatorStats.total_requests > 0
												? Math.round((creatorStats.approved_requests / creatorStats.total_requests) * 100)
												: 0}%
										</span>
									</div>
									<div className="flex justify-between">
										<span className="text-muted-foreground">Coverage Rate</span>
										<span className="font-medium">
											{creatorStats.keys_received > 0
												? Math.round((creatorStats.games_covered / creatorStats.keys_received) * 100)
												: 0}%
										</span>
									</div>
								</div>
							</Card>

							<Card className="p-6">
								<h3 className="text-lg font-semibold mb-4">Activity</h3>
								<div className="space-y-3">
									<div className="flex justify-between">
										<span className="text-muted-foreground">Days Active</span>
										<span className="font-medium">{creatorStats.days_since_signup}</span>
									</div>
									<div className="flex justify-between">
										<span className="text-muted-foreground">Content per Key</span>
										<span className="font-medium">
											{creatorStats.keys_received > 0
												? (creatorStats.content_created / creatorStats.keys_received).toFixed(1)
												: '0.0'}
										</span>
									</div>
								</div>
							</Card>
						</div>
					</TabsContent>

					<TabsContent value="requests" className="space-y-6">
						<KeyRequestsList
							requests={keyRequests}
							onViewTrackingInfo={handleViewTrackingInfo}
							onCancelRequest={handleCancelRequest}
							showCancelButton={true}
						/>
					</TabsContent>

					<TabsContent value="coverage" className="space-y-6">
						<CoverageContent
							coverageItems={coverageItems}
							userProfile={userProfile}
							isLoading={false}
						/>
					</TabsContent>

					<TabsContent value="playtime" className="space-y-6">
						<div className="grid grid-cols-1 gap-6">
							<Card className="p-6">
								<h3 className="text-lg font-semibold mb-4">Games Playtime</h3>
								{gamesWithPlaytime.length > 0 ? (
									<div className="space-y-3">
										{gamesWithPlaytime.map((gamePlaytime) => (
											<div
												key={gamePlaytime.id}
												className="flex flex-col md:flex-row md:items-center justify-between p-4 border rounded-custom-paddings hover:bg-muted/50 transition-colors cursor-pointer gap-3 md:gap-0"
												onClick={() => navigate(`/game/${gamePlaytime.game_id}`)}
											>
												<div className="flex items-center gap-4 flex-1">
													{gamePlaytime.game.cover_image_url ? (
														<img
															src={gamePlaytime.game.cover_image_url}
															alt={gamePlaytime.game.title}
															className="w-32 h-16 rounded-custom-paddings p-0 object-cover"
															onError={(e) => {
																e.currentTarget.src = '/placeholder.svg';
															}}
														/>
													) : (
														<div className="w-12 h-12 rounded bg-muted flex items-center justify-center">
															<Gamepad2 className="w-6 h-6 text-muted-foreground" />
														</div>
													)}
													<div className="flex-1 min-w-0">
														<p className="font-medium truncate">{gamePlaytime.game.title}</p>
														<p className="text-[12px] text-muted-foreground">
															{keyRequests.find(r => r.id === gamePlaytime.id)?.created_at
																? `Key received ${new Date(keyRequests.find(r => r.id === gamePlaytime.id)!.created_at).toLocaleDateString()}`
																: 'Key received'
															}
														</p>
													</div>
												</div>
												{gamePlaytime.playtime > 0 ? (
													<div className="flex flex-col md:flex-row gap-3 md:gap-4 md:ml-4 border-black/20 px-perso-20 rounded-custom-paddings bg-border px-4 py-3">
														<div className="text-left md:text-right">
															<p className="font-semibold text-lg">{formatPlaytime(gamePlaytime.playtime)}</p>
															<p className="text-xs text-muted-foreground">total playtime</p>
														</div>
														{gamePlaytime.playtime_2weeks > 0 && (
															<div className="text-left md:text-right border-t md:border-t-0 border-black/20 md:border-l pt-3 md:pt-0 md:pl-4">
																<p className="font-semibold text-lg">{formatPlaytime(gamePlaytime.playtime_2weeks)}</p>
																<p className="text-xs text-muted-foreground">last 2 weeks</p>
															</div>
														)}
														{gamePlaytime.achievements && (
															<div className="text-left md:text-right border-t md:border-t-0 border-black/20 md:border-l pt-3 md:pt-0 md:pl-4">
																<p className="font-semibold text-lg">
																	{gamePlaytime.achievements.unlocked}/{gamePlaytime.achievements.total}
																</p>
																<p className="text-xs text-muted-foreground">achievements</p>
															</div>
														)}
													</div>
												) : (
													<div className="md:ml-4 text-sm text-muted-foreground">
														No playtime data
													</div>
												)}
											</div>
										))}
									</div>
								) : (
									<div className="text-center py-12">
										<Clock className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
										<p className="text-muted-foreground mb-2">
											No playtime available
										</p>
										<p className="text-sm text-muted-foreground">
											Get approved keys and play games to track your playtime
										</p>
									</div>
								)}
							</Card>
						</div>
					</TabsContent>

					<TabsContent value="badges">
						<Card className="p-6">
							<h3 className="text-lg font-semibold mb-4">
								Badges ({creatorStats.badges.length}/{(() => {
									let total = 0;
									Object.values(CREATOR_BADGE_TIERS).forEach((tiers) => {
										total += tiers.length;
									});
									return total;
								})()})
							</h3>
							<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
								{(Object.entries(CREATOR_BADGE_TIERS) as unknown as [string, { name: string; description: string; icon: string; requirement: any }[]][]).map(([key, tiers]) => {
									return tiers.map((tier, index) => {
										const isEarned = creatorStats.badges.includes(tier.name);

										return (
											<div
												key={`${key}-${index}`}
												className={`p-3 rounded-custom-paddings transition-all ${isEarned
														? 'bg-green-500/5'
														: 'bg-muted/30'
													}`}
											>
												<div className="flex h-full items-center px-2 gap-3">
													<div className="text-2xl flex-shrink-0">{tier.icon}</div>
													<div className="flex-1 min-w-0">
														<p className={`font-medium text-sm ${isEarned ? 'text-green-600 dark:text-green-400' : 'text-foreground/80'
															}`}>
															{tier.name}
														</p>
														<p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
															{tier.description}
														</p>
													</div>
												</div>
											</div>
										);
									});
								})}
							</div>
						</Card>
					</TabsContent>
				</Tabs>
			</div>

			{/* Tracking Info Modal */}
			{selectedGameForTracking && (
				<TrackingInfoModal
					isOpen={isViewingTrackingInfo}
					onClose={() => setIsViewingTrackingInfo(false)}
					game={selectedGameForTracking}
					gameKey={selectedGameForTracking.key}
					userType={userType}
					onStartChat={() => {
						setIsViewingTrackingInfo(false);
						setIsViewingChat(true);
					}}
				/>
			)}

			{/* Game Chat Modal */}
			{selectedGameForTracking && selectedGameForTracking.developer && (
				<GameChatModal
					isOpen={isViewingChat}
					onClose={() => setIsViewingChat(false)}
					game={selectedGameForTracking}
					developer={selectedGameForTracking.developer}
					currentUserId={user?.id || ''}
				/>
			)}
		</Layout>
	);
}

